import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FilePlus2, Landmark, Lock, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { AttachedFiles, EvidencePicker, FINANCE_EVIDENCE, useEvidenceUpload, type AttachedFile } from '../components/EvidenceFiles';
import { useRefusal } from './PartnerForm';
import { formatMonth } from './HistoricalFinance';
import type { PartnerPosition } from './Partners';

/**
 * Financial setup (Finance Phase 1, 2026-10-28): the accounting cutover — the
 * day the books move onto the system — what each cash and bank account held that
 * morning, what partners were owed and owed, and the reconciliation that says
 * what each account should hold today. Historical months never enter it: they
 * were paid before the cutover.
 */

export interface MoneyAccount {
  id: string;
  name: string;
  nameAr: string | null;
  type: 'CASH' | 'BANK';
  currency: string;
  bankName: string | null;
  reference: string | null;
  isActive: boolean;
  notes: string | null;
}

export interface OpeningLine {
  id: string;
  kind: 'CASH' | 'PARTNER_LOAN_PAYABLE' | 'PARTNER_ADVANCE_RECEIVABLE' | 'PARTNER_EQUITY';
  moneyAccountId: string | null;
  partnerId: string | null;
  currency: string;
  amount: string;
  exchangeRate: string;
  baseCurrency: string;
  baseAmount: string;
  notes: string | null;
  moneyAccount: { id: string; name: string; nameAr: string | null; type: string; isActive: boolean } | null;
  partner: { id: string; name: string; nameAr: string | null; status: string } | null;
}

export interface SetupIssue {
  code: string;
  severity: 'BLOCKING' | 'WARNING';
  ref?: { type: string; id: string; label?: string };
  details?: Record<string, any>;
}

export interface Reconciliation {
  cutover: { id: string; cutoverDate: string; status: 'DRAFT' | 'CONFIRMED'; baseCurrency: string; version: number };
  baseCurrency: string;
  today: string;
  cash: {
    accounts: Array<{ moneyAccountId: string; name: string; currency: string; isActive: boolean; opening: string | null; inflow: string; outflow: string; movements: number; expected: string }>;
    unassigned: Array<{ source: string; currency: string; inflow: string; outflow: string; count: number }>;
    currencies: Array<{ currency: string; opening: string; inflow: string; outflow: string; expected: string }>;
  };
  partners: Array<{ partner: { id: string; name: string; nameAr: string | null }; positions: PartnerPosition[] }>;
  issues: SetupIssue[];
  blocked: boolean;
}

export interface SetupOverview {
  baseCurrency: string;
  today: string;
  cutover:
    | null
    | {
        id: string;
        cutoverDate: string;
        baseCurrency: string;
        status: 'DRAFT' | 'CONFIRMED';
        notes: string | null;
        version: number;
        confirmedAt: string | null;
        reopenedAt: string | null;
        reopenReason: string | null;
        lines: OpeningLine[];
        attachments: AttachedFile[];
      };
  accounts: MoneyAccount[];
  partners: Array<{ id: string; name: string; nameAr: string | null; status: string }>;
  reconciliation: Reconciliation | null;
}

export function useSetupOverview() {
  return useQuery({ queryKey: ['financial-setup'], queryFn: () => api.get<SetupOverview>('/financial-setup') });
}

/** An issue, in the reader's language. */
export function IssueText({ issue }: { issue: SetupIssue }) {
  const { t, locale } = useI18n();
  const d = issue.details ?? {};
  const vars: Record<string, string | number> = {
    label: issue.ref?.label ?? '',
    month: d.month ? formatMonth(d.month, locale) : '',
    valuedIn: d.valuedIn ?? '',
    baseCurrency: d.baseCurrency ?? '',
    count: d.count ?? 0,
    expected: d.expected && d.currency ? formatMoney(d.expected, d.currency, locale) : '',
  };
  return <>{t(`setup.issue.${issue.code}` as TranslationKey, vars)}</>;
}

export function IssueList({ issues }: { issues: SetupIssue[] }) {
  const { t } = useI18n();
  if (issues.length === 0)
    return (
      <p className="flex items-center gap-2 text-body-sm text-success-fg dark:text-success">
        <CheckCircle2 className="h-4 w-4" aria-hidden />
        {t('setup.issuesNone')}
      </p>
    );
  const ordered = [...issues].sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'BLOCKING' ? -1 : 1));
  return (
    <ul className="space-y-2">
      {ordered.map((issue, i) => (
        <li key={i} className="flex items-start gap-2 text-body-sm">
          <Badge tone={issue.severity === 'BLOCKING' ? 'error' : 'warning'} className="shrink-0">
            {issue.severity === 'BLOCKING' ? t('setup.blocking') : t('setup.warning')}
          </Badge>
          <span>
            {issue.ref?.type === 'HistoricalFinancialRecord' ? (
              <Link to={`/historical/${issue.ref.id}`} className="hover:text-primary"><IssueText issue={issue} /></Link>
            ) : issue.ref?.type === 'Partner' ? (
              <Link to={`/partners/${issue.ref.id}`} className="hover:text-primary"><IssueText issue={issue} /></Link>
            ) : (
              <IssueText issue={issue} />
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Opening + in − out per account, the unassigned flows, and each currency's total. */
export function ReconciliationTables({ data }: { data: Reconciliation }) {
  const { t, locale } = useI18n();
  return (
    <div className="space-y-5">
      <Table>
        <thead>
          <tr>
            <Th>{t('setup.accounts')}</Th>
            <Th align="end">{t('setup.openingCol')}</Th>
            <Th align="end">{t('setup.inCol')}</Th>
            <Th align="end">{t('setup.outCol')}</Th>
            <Th align="end">{t('setup.expectedCol')}</Th>
          </tr>
        </thead>
        <tbody>
          {data.cash.accounts.map((a) => (
            <Tr key={a.moneyAccountId}>
              <Td>
                {a.name}
                {!a.isActive && <Badge tone="neutral" className="ms-2">{t('setup.accountClosed')}</Badge>}
                <span className="block text-caption text-faint tabular">{a.movements} {t('setup.movementsCol')}</span>
              </Td>
              <Td align="end" className="tabular">{a.opening === null ? <span className="text-caption text-faint">{t('setup.noOpening')}</span> : formatMoney(a.opening, a.currency, locale)}</Td>
              <Td align="end" className="tabular">{formatMoney(a.inflow, a.currency, locale)}</Td>
              <Td align="end" className="tabular">{formatMoney(a.outflow, a.currency, locale)}</Td>
              <Td align="end" className="tabular font-medium">{formatMoney(a.expected, a.currency, locale)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {data.cash.unassigned.length > 0 && (
        <div>
          <p className="mb-1 text-body-sm font-medium text-[var(--text-primary)]">{t('setup.unassigned')}</p>
          <p className="mb-2 text-caption text-muted">{t('setup.unassignedHint')}</p>
          <Table>
            <tbody>
              {data.cash.unassigned.map((u, i) => (
                <Tr key={i}>
                  <Td>{t(`setup.source.${u.source}` as TranslationKey)} <span className="text-caption text-faint tabular">· {u.count}</span></Td>
                  <Td align="end" className="tabular">{Number(u.inflow) ? `+ ${formatMoney(u.inflow, u.currency, locale)}` : ''}</Td>
                  <Td align="end" className="tabular">{Number(u.outflow) ? `− ${formatMoney(u.outflow, u.currency, locale)}` : ''}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div>
        <p className="mb-2 text-body-sm font-medium text-[var(--text-primary)]">{t('setup.byCurrency')}</p>
        <Table>
          <thead>
            <tr>
              <Th>{t('common.currency')}</Th>
              <Th align="end">{t('setup.openingCol')}</Th>
              <Th align="end">{t('setup.inCol')}</Th>
              <Th align="end">{t('setup.outCol')}</Th>
              <Th align="end">{t('setup.expectedCol')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.cash.currencies.map((c) => (
              <Tr key={c.currency}>
                <Td>{c.currency}</Td>
                <Td align="end" className="tabular">{formatMoney(c.opening, c.currency, locale)}</Td>
                <Td align="end" className="tabular">{formatMoney(c.inflow, c.currency, locale)}</Td>
                <Td align="end" className="tabular">{formatMoney(c.outflow, c.currency, locale)}</Td>
                <Td align="end" className="tabular font-medium">{formatMoney(c.expected, c.currency, locale)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

/** Route: `/financial-setup`. */
export function FinancialSetupPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refusal = useRefusal('setup');
  const { data, isLoading, error, refetch } = useSetupOverview();
  const upload = useEvidenceUpload(FINANCE_EVIDENCE);
  const manage = can(P.OPENING_BALANCES_MANAGE);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['financial-setup'] });

  const addFiles = useMutation({
    mutationFn: () => api.post('/financial-setup/attachments', { fileIds: upload.ids }),
    onSuccess: () => {
      upload.clear();
      refresh();
    },
    onError: (e) => toast.error(refusal(e)),
  });
  const [removingFile, setRemovingFile] = useState<string | null>(null);
  const removeFile = useMutation({
    mutationFn: (aid: string) => api.del(`/financial-setup/attachments/${aid}`),
    onMutate: (aid) => setRemovingFile(aid),
    onSettled: () => setRemovingFile(null),
    onSuccess: refresh,
    onError: (e) => toast.error(refusal(e)),
  });
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const removeLine = useMutation({
    mutationFn: (lineId: string) => api.del(`/financial-setup/opening-balances/${lineId}`),
    onSuccess: () => {
      setConfirmRemove(null);
      refresh();
    },
    onError: (e) => toast.error(refusal(e)),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const cutover = data.cutover;
  const draft = cutover?.status === 'DRAFT';
  const cashLines = cutover?.lines.filter((l) => l.kind === 'CASH') ?? [];
  const partnerLines = cutover?.lines.filter((l) => l.kind !== 'CASH') ?? [];
  const rec = data.reconciliation;

  const lineActions = (line: OpeningLine) =>
    manage &&
    draft && (
      <span className="inline-flex gap-1">
        <Button size="sm" variant="ghost" onClick={() => navigate(`/financial-setup/opening/${line.id}/edit`)} title={t('common.edit')}>
          <Pencil className="h-4 w-4" aria-hidden />
          <span className="sr-only">{t('common.edit')}</span>
        </Button>
        {confirmRemove === line.id ? (
          <Button size="sm" variant="danger" loading={removeLine.isPending} onClick={() => removeLine.mutate(line.id)}>
            {t('setup.removeLineConfirm')}
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(line.id)} title={t('setup.removeLine')}>
            <Trash2 className="h-4 w-4" aria-hidden />
            <span className="sr-only">{t('setup.removeLine')}</span>
          </Button>
        )}
      </span>
    );

  const valuedIn = (line: OpeningLine) =>
    line.baseCurrency !== data.baseCurrency ? (
      <Badge tone="error" className="ms-2">{t('setup.lineOldBase', { currency: line.baseCurrency })}</Badge>
    ) : null;

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {t('setup.title')}
            {cutover && <Badge tone={cutover.status === 'CONFIRMED' ? 'success' : 'warning'}>{t(`setup.status.${cutover.status}` as TranslationKey)}</Badge>}
          </span>
        }
        subtitle={t('setup.subtitle')}
        action={
          manage && (
            <>
              {(!cutover || draft) && (
                <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate('/financial-setup/cutover')}>
                  {cutover ? t('setup.changeCutover') : t('setup.setCutover')}
                </Button>
              )}
              {draft && (
                <Button variant="primary" icon={<Lock className="h-4 w-4" />} onClick={() => navigate('/financial-setup/confirm')}>
                  {t('setup.confirm')}
                </Button>
              )}
              {cutover?.status === 'CONFIRMED' && (
                <Button icon={<RotateCcw className="h-4 w-4" />} onClick={() => navigate('/financial-setup/reopen')}>
                  {t('setup.reopen')}
                </Button>
              )}
            </>
          )
        }
      />

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('setup.cutover')} subtitle={t('setup.cutoverHint')} />
          {cutover ? (
            <>
              <dl>
                <Detail label={t('setup.cutoverDate')}><span className="tabular">{formatDate(cutover.cutoverDate, locale)}</span></Detail>
                <Detail label={t('setup.baseCurrency')}>{data.baseCurrency}</Detail>
                <Detail label={t('common.status')}>
                  {cutover.status === 'CONFIRMED' && cutover.confirmedAt
                    ? t('setup.confirmedBy', { date: formatDateTime(cutover.confirmedAt, locale) })
                    : t('setup.status.DRAFT')}
                </Detail>
              </dl>
              {cutover.reopenedAt && (
                <p className="mt-2 text-caption text-muted" dir="auto">
                  {t('setup.reopenedBy', { date: formatDateTime(cutover.reopenedAt, locale), reason: cutover.reopenReason ?? '' })}
                </p>
              )}
              {cutover.notes && <p className="mt-2 whitespace-pre-line text-body-sm text-muted" dir="auto">{cutover.notes}</p>}
            </>
          ) : (
            <p className="text-body-sm text-muted">{t('setup.noCutover')}</p>
          )}
        </Card>
        <AdvanceLimitCard baseCurrency={data.baseCurrency} />
      </div>

      <Card className="mb-5" padded={false}>
        <CardHeader
          title={t('setup.accounts')}
          className="p-5 pb-0"
          action={
            manage && (
              <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/financial-setup/accounts/new')}>
                {t('setup.newAccount')}
              </Button>
            )
          }
        />
        {data.accounts.length === 0 ? (
          <p className="px-5 pb-5 text-body-sm text-muted">{t('setup.noAccounts')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('setup.accountName')}</Th>
                <Th>{t('setup.accountType')}</Th>
                <Th>{t('common.currency')}</Th>
                <Th>{t('setup.reference')}</Th>
                <Th>{t('common.status')}</Th>
                {manage && <Th align="end">{t('common.actions')}</Th>}
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => (
                <Tr key={a.id}>
                  <Td>
                    <Landmark className="me-1.5 inline h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
                    {localized(a)}
                    {a.bankName && <span className="block text-caption text-faint">{a.bankName}</span>}
                  </Td>
                  <Td>{t(a.type === 'BANK' ? 'setup.accountBANK' : 'setup.accountCASH')}</Td>
                  <Td>{a.currency}</Td>
                  <Td className="text-muted"><span dir="auto">{a.reference ?? '—'}</span></Td>
                  <Td><Badge tone={a.isActive ? 'success' : 'neutral'}>{a.isActive ? t('setup.accountOpen') : t('setup.accountClosed')}</Badge></Td>
                  {manage && (
                    <Td align="end">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/financial-setup/accounts/${a.id}/edit`)}>
                        {t('common.edit')}
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {cutover && (
        <Card className="mb-5" padded={false}>
          <CardHeader
            title={t('setup.opening')}
            subtitle={formatDate(cutover.cutoverDate, locale)}
            className="p-5 pb-0"
            action={
              manage &&
              draft && (
                <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/financial-setup/opening/new')}>
                  {t('setup.addLine')}
                </Button>
              )
            }
          />
          {cutover.lines.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('setup.noLines')}</p>
          ) : (
            <>
              <p className="px-5 pb-1 text-overline uppercase text-[var(--text-tertiary)]">{t('setup.openingCash')}</p>
              <Table>
                <tbody>
                  {cashLines.map((l) => (
                    <Tr key={l.id}>
                      <Td>{l.moneyAccount ? localized(l.moneyAccount) : '—'}{valuedIn(l)}</Td>
                      <Td align="end" className="tabular">{formatMoney(l.amount, l.currency, locale)}</Td>
                      <Td align="end" className="tabular text-muted">{l.currency !== l.baseCurrency ? formatMoney(l.baseAmount, l.baseCurrency, locale) : ''}</Td>
                      <Td align="end">{lineActions(l)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              {partnerLines.length > 0 && (
                <>
                  <p className="px-5 pb-1 pt-4 text-overline uppercase text-[var(--text-tertiary)]">{t('setup.openingPartners')}</p>
                  <Table>
                    <tbody>
                      {partnerLines.map((l) => (
                        <Tr key={l.id}>
                          <Td>
                            {l.partner ? <Link to={`/partners/${l.partner.id}`} className="hover:text-primary">{localized(l.partner)}</Link> : '—'}
                            <span className="block text-caption text-muted">{t(`setup.kind.${l.kind}` as TranslationKey)}</span>
                            {valuedIn(l)}
                          </Td>
                          <Td align="end" className="tabular">{formatMoney(l.amount, l.currency, locale)}</Td>
                          <Td align="end" className="tabular text-muted">{l.currency !== l.baseCurrency ? formatMoney(l.baseAmount, l.baseCurrency, locale) : ''}</Td>
                          <Td align="end">{lineActions(l)}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Card>
      )}

      {cutover && (
        <Card className="mb-5">
          <CardHeader title={t('setup.files')} subtitle={t('setup.filesHint')} />
          <AttachedFiles files={cutover.attachments} onRemove={manage ? (aid) => removeFile.mutate(aid) : undefined} removing={removingFile} />
          {manage && (
            <div className="mt-4">
              <EvidencePicker upload={upload} policy={FINANCE_EVIDENCE} />
              {upload.files.length > 0 && (
                <Button className="mt-3" variant="primary" size="sm" icon={<FilePlus2 className="h-4 w-4" />} loading={addFiles.isPending} disabled={upload.busy} onClick={() => addFiles.mutate()}>
                  {t('common.add')}
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {rec && (
        <>
          <Card className="mb-5">
            <CardHeader
              title={t('setup.issues')}
              action={rec.blocked ? <AlertTriangle className="h-5 w-5 text-error-fg dark:text-error-dark" aria-hidden /> : undefined}
            />
            <IssueList issues={rec.issues} />
          </Card>
          <Card className="mb-5">
            <CardHeader title={t('setup.reconciliation')} subtitle={t('setup.reconciliationHint')} />
            <ReconciliationTables data={rec} />
          </Card>
          {rec.partners.length > 0 && (
            <Card padded={false}>
              <CardHeader title={t('setup.partnersPreview')} className="p-5 pb-0" />
              <Table>
                <thead>
                  <tr>
                    <Th>{t('ptx.partner')}</Th>
                    <Th align="end">{t('partners.loanPayable')}</Th>
                    <Th align="end">{t('partners.advanceReceivable')}</Th>
                    <Th align="end">{t('partners.equity')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rec.partners.flatMap(({ partner, positions }) =>
                    positions.map((p) => (
                      <Tr key={`${partner.id}-${p.currency}`}>
                        <Td><Link to={`/partners/${partner.id}`} className="hover:text-primary">{localized(partner)}</Link></Td>
                        <Td align="end" className="tabular">{formatMoney(p.loanPayable, p.currency, locale)}</Td>
                        <Td align="end" className="tabular">{formatMoney(p.advanceReceivable, p.currency, locale)}</Td>
                        <Td align="end" className="tabular">{formatMoney(p.equityContributed, p.currency, locale)}</Td>
                      </Tr>
                    )),
                  )}
                </tbody>
              </Table>
            </Card>
          )}
        </>
      )}
    </>
  );
}

/** Settings › `finance.partnerAdvanceLimit`, kept beside the setup it polices. */
function AdvanceLimitCard({ baseCurrency }: { baseCurrency: string }) {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings') }).data;
  const current = Number(settings?.finance?.['finance.partnerAdvanceLimit'] ?? 0);
  const [value, setValue] = useState('');
  useEffect(() => setValue(String(current)), [current]);
  const editable = can(P.FINANCE_SETTINGS_MANAGE);

  const save = useMutation({
    mutationFn: () => api.patch('/settings/finance.partnerAdvanceLimit', { value: Number(value) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success(t('setup.saved'));
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader title={t('setup.policy')} subtitle={t('setup.advanceLimitHint', { base: baseCurrency })} />
      <p className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{t('setup.advanceLimit')}</p>
      {editable ? (
        <div className="flex gap-2">
          <Input type="number" min={0} step="0.01" dir="ltr" value={value} onChange={(e) => setValue(e.target.value)} className="max-w-xs" />
          <Button loading={save.isPending} disabled={value === '' || Number(value) < 0 || Number(value) === current} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </div>
      ) : (
        <p className="tabular">{current > 0 ? formatMoney(current, baseCurrency, locale) : '—'}</p>
      )}
    </Card>
  );
}
